import apiService from './api'
import type {
  ReminderExecutionResult,
  ReminderStatusResponse,
  ReminderStatsResponse,
  ReminderExecuteNowResponse,
  ReminderUpdateScheduleResponse,
  ReminderToggleResponse,
  ReminderHealthResponse,
  ReminderUpdateScheduleRequest,
  ReminderExecuteNowRequest,
  CronPreset,
  DaysBeforePreset,
  ValidationResult,
  StatusDisplayInfo,
  AllTenantsConfigResponse,
  TenantReminderStatusResponse,
} from '../types/reminder'

/**
 * 自動期限リマインダー管理API クライアント
 *
 * 全メソッドはCORPORATE_ADMIN以上の権限が必要
 */
export class ReminderService {
  private static readonly API_BASE = '/reminder'

  /**
   * スケジューラーの状態とシステム情報を取得
   * GET /api/reminder/status
   */
  static async getStatus(): Promise<ReminderStatusResponse> {
    try {
      return await apiService.get<ReminderStatusResponse>(`${this.API_BASE}/status`)
    } catch (error) {
      console.error('Failed to get reminder status:', error)
      throw error
    }
  }

  /**
   * 実行統計・ダッシュボード情報を取得
   * GET /api/reminder/stats
   */
  static async getStats(): Promise<ReminderStatsResponse> {
    try {
      return await apiService.get<ReminderStatsResponse>(`${this.API_BASE}/stats`)
    } catch (error) {
      console.error('Failed to get reminder stats:', error)
      throw error
    }
  }

  /**
   * システムのヘルスチェック
   * GET /api/reminder/health
   */
  static async getHealth(): Promise<ReminderHealthResponse> {
    try {
      return await apiService.get<ReminderHealthResponse>(`${this.API_BASE}/health`)
    } catch (error) {
      console.error('Failed to get reminder health:', error)
      throw error
    }
  }

  /**
   * 手動でリマインダーを実行
   * POST /api/reminder/execute-now
   */
  static async executeNow(
    request?: ReminderExecuteNowRequest
  ): Promise<ReminderExecuteNowResponse> {
    try {
      console.log('🔔 Executing reminder manually:', request)
      return await apiService.post<ReminderExecuteNowResponse>(
        `${this.API_BASE}/execute-now`,
        request || {}
      )
    } catch (error) {
      console.error('Failed to execute reminder now:', error)
      throw error
    }
  }

  /**
   * スケジュール設定を変更
   * PUT /api/reminder/schedule
   */
  static async updateSchedule(
    request: ReminderUpdateScheduleRequest
  ): Promise<ReminderUpdateScheduleResponse> {
    try {
      console.log('🔔 Updating reminder schedule:', request)
      return await apiService.put<ReminderUpdateScheduleResponse>(
        `${this.API_BASE}/schedule`,
        request
      )
    } catch (error) {
      console.error('Failed to update reminder schedule:', error)
      throw error
    }
  }

  /**
   * 自動リマインダーシステムを有効化
   * POST /api/reminder/enable
   */
  static async enable(): Promise<ReminderToggleResponse> {
    try {
      console.log('🔔 Enabling reminder system')
      return await apiService.post<ReminderToggleResponse>(`${this.API_BASE}/enable`)
    } catch (error) {
      console.error('Failed to enable reminder system:', error)
      throw error
    }
  }

  /**
   * 自動リマインダーシステムを無効化
   * POST /api/reminder/disable
   */
  static async disable(): Promise<ReminderToggleResponse> {
    try {
      console.log('🔔 Disabling reminder system')
      return await apiService.post<ReminderToggleResponse>(`${this.API_BASE}/disable`)
    } catch (error) {
      console.error('Failed to disable reminder system:', error)
      throw error
    }
  }

  // ========================================
  // SUPER_ADMIN専用メソッド（テナント管理）
  // ========================================

  /**
   * 全テナントのリマインダー設定一覧を取得
   * GET /api/reminder/tenants
   * SUPER_ADMIN専用
   */
  static async getAllTenantsConfig(): Promise<AllTenantsConfigResponse> {
    try {
      return await apiService.get<AllTenantsConfigResponse>(`${this.API_BASE}/tenants`)
    } catch (error) {
      console.error('Failed to get all tenants config:', error)
      throw error
    }
  }

  /**
   * 特定テナントのリマインダーステータスを取得
   * GET /api/reminder/tenants/:tenantId
   * SUPER_ADMIN専用
   */
  static async getStatusForTenant(tenantId: string): Promise<TenantReminderStatusResponse> {
    try {
      return await apiService.get<TenantReminderStatusResponse>(
        `${this.API_BASE}/tenants/${tenantId}`
      )
    } catch (error) {
      console.error('Failed to get tenant reminder status:', error)
      throw error
    }
  }

  /**
   * 特定テナントのスケジュール設定を更新
   * PUT /api/reminder/tenants/:tenantId
   * SUPER_ADMIN専用
   */
  static async updateScheduleForTenant(
    tenantId: string,
    request: ReminderUpdateScheduleRequest
  ): Promise<ReminderUpdateScheduleResponse> {
    try {
      console.log('🔔 Updating reminder schedule for tenant:', tenantId, request)
      return await apiService.put<ReminderUpdateScheduleResponse>(
        `${this.API_BASE}/tenants/${tenantId}`,
        request
      )
    } catch (error) {
      console.error('Failed to update tenant reminder schedule:', error)
      throw error
    }
  }

  /**
   * 特定テナントのリマインダーを手動実行
   * POST /api/reminder/tenants/:tenantId/execute
   * SUPER_ADMIN専用
   */
  static async executeNowForTenant(tenantId: string): Promise<ReminderExecuteNowResponse> {
    try {
      console.log('🔔 Executing reminder for tenant:', tenantId)
      return await apiService.post<ReminderExecuteNowResponse>(
        `${this.API_BASE}/tenants/${tenantId}/execute`
      )
    } catch (error) {
      console.error('Failed to execute reminder for tenant:', error)
      throw error
    }
  }

  /**
   * 特定テナントのリマインダーを有効化
   * POST /api/reminder/tenants/:tenantId/enable
   * SUPER_ADMIN専用
   */
  static async enableForTenant(tenantId: string): Promise<ReminderToggleResponse> {
    try {
      console.log('🔔 Enabling reminder for tenant:', tenantId)
      return await apiService.post<ReminderToggleResponse>(
        `${this.API_BASE}/tenants/${tenantId}/enable`
      )
    } catch (error) {
      console.error('Failed to enable reminder for tenant:', error)
      throw error
    }
  }

  /**
   * 特定テナントのリマインダーを無効化
   * POST /api/reminder/tenants/:tenantId/disable
   * SUPER_ADMIN専用
   */
  static async disableForTenant(tenantId: string): Promise<ReminderToggleResponse> {
    try {
      console.log('🔔 Disabling reminder for tenant:', tenantId)
      return await apiService.post<ReminderToggleResponse>(
        `${this.API_BASE}/tenants/${tenantId}/disable`
      )
    } catch (error) {
      console.error('Failed to disable reminder for tenant:', error)
      throw error
    }
  }

  /**
   * cron式の検証（クライアントサイド）
   */
  static validateCronExpression(cronExpression: string): ValidationResult {
    // 基本的なcron式のパターンをチェック
    const cronPattern =
      /^(\*|[0-9]|[1-5][0-9])(\s+)(\*|[0-9]|1[0-9]|2[0-3])(\s+)(\*|[1-9]|[12][0-9]|3[01])(\s+)(\*|[1-9]|1[0-2])(\s+)(\*|[0-6])$/

    if (!cronExpression || cronExpression.trim() === '') {
      return {
        valid: false,
        error: 'cron式は必須です',
        example: '0 10 * * * (毎日10時)',
      }
    }

    const trimmed = cronExpression.trim()
    const parts = trimmed.split(/\s+/)

    if (parts.length !== 5) {
      return {
        valid: false,
        error: 'cron式は5つの部分（分 時 日 月 曜日）で構成される必要があります',
        example: '0 10 * * * (毎日10時)',
      }
    }

    // より詳細な検証はサーバーサイドで行う
    if (cronPattern.test(trimmed)) {
      return { valid: true }
    }

    return {
      valid: false,
      error: 'cron式の形式が正しくありません',
      example: '0 10 * * * (毎日10時), 30 14 * * 1 (毎週月曜14:30)',
    }
  }

  /**
   * 日数配列の検証
   */
  static validateDaysBefore(daysBefore: number[]): ValidationResult {
    if (!Array.isArray(daysBefore) || daysBefore.length === 0) {
      return {
        valid: false,
        error: '日数は1つ以上指定する必要があります',
      }
    }

    for (const days of daysBefore) {
      if (!Number.isInteger(days) || days < 0 || days > 30) {
        return {
          valid: false,
          error: '日数は0-30の整数で指定してください',
        }
      }
    }

    // 重複チェック
    const uniqueDays = Array.from(new Set(daysBefore))
    if (uniqueDays.length !== daysBefore.length) {
      return {
        valid: false,
        error: '重複した日数が含まれています',
      }
    }

    return { valid: true }
  }

  /**
   * 一般的なcron式のプリセット
   */
  static getCronPresets(): CronPreset[] {
    return [
      {
        label: '毎日朝10時',
        value: '0 10 * * *',
        description: '毎日午前10時に実行',
      },
      {
        label: '毎日夕方6時',
        value: '0 18 * * *',
        description: '毎日午後6時に実行',
      },
      {
        label: '平日朝9時',
        value: '0 9 * * 1-5',
        description: '月曜日から金曜日の午前9時に実行',
      },
      {
        label: '毎週月曜日朝10時',
        value: '0 10 * * 1',
        description: '毎週月曜日の午前10時に実行',
      },
      {
        label: '1時間ごと',
        value: '0 * * * *',
        description: '毎時0分に実行（テスト用）',
      },
    ]
  }

  /**
   * 日数設定のプリセット
   */
  static getDaysBeforePresets(): DaysBeforePreset[] {
    return [
      {
        label: '標準 (7日, 3日, 1日前)',
        value: [7, 3, 1],
        description: '期限の7日前、3日前、1日前に通知',
      },
      {
        label: '早期 (14日, 7日, 3日, 1日前)',
        value: [14, 7, 3, 1],
        description: '期限の2週間前から頻繁に通知',
      },
      {
        label: '直前のみ (3日, 1日前)',
        value: [3, 1],
        description: '期限直前のみ通知',
      },
      {
        label: '1週間前のみ',
        value: [7],
        description: '期限の1週間前に1回のみ通知',
      },
      {
        label: '毎日 (7日〜1日前)',
        value: [7, 6, 5, 4, 3, 2, 1],
        description: '期限の1週間前から毎日通知',
      },
    ]
  }

  /**
   * ステータス表示用のヘルパー
   */
  static getStatusDisplayInfo(health: ReminderHealthResponse): StatusDisplayInfo {
    const isHealthy = health.status === 'healthy'
    const statusColor = isHealthy ? 'primary' : health.status === 'degraded' ? 'secondary' : 'error'
    const statusText = isHealthy ? '正常' : health.status === 'degraded' ? '一部異常' : 'エラー'

    return {
      color: statusColor,
      text: statusText,
      isHealthy,
    }
  }

  /**
   * 実行結果のサマリー生成
   */
  static generateExecutionSummary(result: ReminderExecutionResult): string {
    if (result.totalTargeted === 0) {
      return 'リマインダー送信対象がありませんでした'
    }

    const successRate = Math.round((result.totalSent / result.totalTargeted) * 100)
    return `${result.totalTargeted}件中${result.totalSent}件送信完了（成功率: ${successRate}%）`
  }
}

export default ReminderService
